import React, {
  createContext, FC, useContext, useReducer,
} from 'react';
import { ActionFromPayloadMap, PageLayout, SummariesLayout } from '../config/types';
import {
  INITIAL_DONT_SHOW_PRESENTER_SETTING,
  INITIAL_VISIBLE_PRESENTER_COMPONENTS,
  INITIAL_SUMMARIES_LAYOUT,
} from '../config/constants';
import LayoutReducer from '../reducers/layout';

export type LayoutStateType = {
  pageLayout: PageLayout
  summariesLayout: SummariesLayout
  assetDetailsAssetId: string | null
  assetDetailsLayout: PageLayout
  presenterModeActive: boolean
  visiblePresenterComponents: {
    [projectId: string]: {
      [assetType: string]: Array<string>
    }
  } | null
  dontShowPresenterSetting: boolean
  lastAssetDetailsAssetIdUpdate?: Date,
};

const urlParams = new URLSearchParams(window.location.search);

const defaultParams = {
  pageLayout: 'assetTable',
  summariesLayout: 'default',
  assetDetailsLayout: 'assetTable',
  presenterModeActive: 'false',
  visiblePresenterComponents: 'null',
  lastAssetDetailsAssetIdUpdate: new Date(),
};

const getLayoutParam = (key: string) => urlParams.get(key) ?? defaultParams[key];

const setLayoutPram = (url: URL, key: string, value: any) => ((value === defaultParams[key])
  ? url.searchParams.delete(key)
  : url.searchParams.set(key, value));

const initialState: LayoutStateType = {
  pageLayout: getLayoutParam('pageLayout') as PageLayout,
  summariesLayout: getLayoutParam('summariesLayout') || INITIAL_SUMMARIES_LAYOUT,
  assetDetailsAssetId: null,
  assetDetailsLayout: getLayoutParam('assetDetailsLayout'),
  presenterModeActive: getLayoutParam('presenterModeActive') === 'true',
  visiblePresenterComponents: (
    INITIAL_VISIBLE_PRESENTER_COMPONENTS && JSON.parse(INITIAL_VISIBLE_PRESENTER_COMPONENTS)
  ) || null,
  dontShowPresenterSetting: INITIAL_DONT_SHOW_PRESENTER_SETTING,
};

const setLayoutSearchParams = (state: LayoutStateType) => {
  const url = new URL(window.location.href);
  setLayoutPram(url, 'pageLayout', state.pageLayout);
  setLayoutPram(url, 'summariesLayout', state.summariesLayout.toString());
  setLayoutPram(url, 'assetDetailsLayout', state.assetDetailsLayout.toString());
  setLayoutPram(url, 'presenterModeActive', state.presenterModeActive.toString());
  window.history.pushState(null, '', url);
};

export type LayoutActionPayloadMap = {
  SET_PAGE_LAYOUT: PageLayout;
  SET_ASSET_DETAILS_LAYOUT: PageLayout;
  SET_PRESENTER_MODE: boolean;
  SET_VISIBLE_PRESENTER_COMPONENTS: LayoutStateType['visiblePresenterComponents'];
  SET_DONT_SHOW_PRESENTER_SETTING: boolean;
  SET_SUMMARIES_LAYOUT: SummariesLayout;
  SET_ASSET_DETAILS_ID: { assetDetailsAssetId: string | null; manual: boolean } | null;
};

export type LayoutAction = ActionFromPayloadMap<LayoutActionPayloadMap>;

export const LayoutContext = createContext({
  state: initialState,
  dispatch: (() => { }) as React.Dispatch<LayoutAction>,
});

export const useLayout = (): [LayoutStateType, React.Dispatch<LayoutAction>] => {
  const { state, dispatch } = useContext(LayoutContext);
  return [state, dispatch];
};

export const LayoutProvider:FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [state, dispatch] = useReducer(LayoutReducer, initialState);

  setLayoutSearchParams(state);
  return (
    <LayoutContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        state,
        dispatch,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};
